import {
    getAuth,
    browserLocalPersistence,
    connectAuthEmulator,
    onAuthStateChanged,
    setPersistence,
    signOut,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signInWithPopup,
    GoogleAuthProvider,
} from "firebase/auth";
import {Store} from "@/plugins/Store";
import {User} from "@/model/User";
import {FirebaseApp} from "@/plugins/Firebase";
import {captureException} from "@/plugins/Sentry";
import {Toast} from "@/model/Toast";
import {ciEmulatorPorts} from "@/plugins/ciEmulators";

const auth = getAuth(FirebaseApp);
const ciEmulators = ciEmulatorPorts();
if (ciEmulators) {
    connectAuthEmulator(auth, ciEmulators.authUrl, {disableWarnings: true});
}

function errorCode(error: unknown): string {
    if (error && typeof error === 'object' && 'code' in error) {
        const code = (error as {code?: unknown}).code;
        return typeof code === 'string' ? code : '';
    }
    return '';
}

function mapAuthError(error: unknown, context: 'password' | 'google' = 'password'): string {
    switch (errorCode(error)) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
            return 'auth.errors.invalidCredential';
        case 'auth/user-not-found':
            return 'auth.errors.userNotFound';
        case 'auth/invalid-email':
            return 'auth.errors.invalidEmail';
        case 'auth/too-many-requests':
            return 'auth.errors.tooManyRequests';
        case 'auth/user-disabled':
            return 'auth.errors.userDisabled';
        case 'auth/network-request-failed':
            return 'auth.errors.network';
        case 'auth/popup-blocked':
            return 'auth.errors.popupBlocked';
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return 'auth.errors.googleFailed';
        case 'auth/unauthorized-domain':
        case 'auth/operation-not-supported-in-this-environment':
            return 'auth.errors.googleFailed';
        case 'auth/account-exists-with-different-credential':
        case 'auth/email-already-in-use':
            return 'auth.errors.emailAlreadyInUse';
        case 'auth/weak-password':
            return 'auth.errors.weakPassword';
        default:
            captureException(error);
            return context === 'google' ? 'auth.errors.googleFailed' : 'auth.errors.network';
    }
}

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({prompt: 'select_account'});

setPersistence(auth, browserLocalPersistence).catch((error) => {
    Store.dispatch('toasts/add', {
        type: 'error',
        title: 'unknownError',
    } as Toast);
    captureException(error);
});

onAuthStateChanged(auth, user => {
    if (user) {
        Store.dispatch('login', new User(
            user.uid,
            user.displayName || user.email || '',
            user.email || ''
        ));
        return;
    }
    Store.dispatch('noUser');
});

const Auth = {
    signInWithEmail: async function (email: string, password: string): Promise<void> {
        try {
            await signInWithEmailAndPassword(auth, email.trim(), password);
        } catch (error) {
            throw mapAuthError(error, 'password');
        }
    },

    /** The new account is signed in; onAuthStateChanged runs the usual login (users/{uid}, plan). */
    signUpWithEmail: async function (email: string, password: string): Promise<void> {
        try {
            await createUserWithEmailAndPassword(auth, email.trim(), password);
        } catch (error) {
            throw mapAuthError(error, 'password');
        }
    },

    /** Resolves for an unknown e-mail too, so the form never tells whether an account exists. */
    sendPasswordReset: async function (email: string): Promise<void> {
        try {
            await sendPasswordResetEmail(auth, email.trim());
        } catch (error) {
            if (errorCode(error) === 'auth/user-not-found') {
                return;
            }
            throw mapAuthError(error, 'password');
        }
    },

    signInWithGoogle: async function (): Promise<void> {
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (error) {
            throw mapAuthError(error, 'google');
        }
    },

    get isAuthenticated(): boolean {
        return Store.getters.authenticated;
    },

    logout: function (): Promise<void> {
        return signOut(auth).catch(() => undefined);
    }
};

export {Auth};
